import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWaterUsage,
  addWaterToBusiness,
  updateWaterUsed,
  deleteWater,
} from "../../../../../../apis/Business/EnergyApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const DrinkingWaterTable = ({ businessId, category }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});

  const [dropdownUsedFor, setDropdownUsedFor] = useState([
    {
      id: 1,
      name: t("DRINKING_WATER_EUROPE"),
      value: t("DRINKING_WATER_EUROPE"),
      type: t("DRINKING_WATER_EUROPE"),
      unit: "kg",
      typeDB: "drinking_water",
      key: "DRINKING_WATER_EUROPE",
    },
  ]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  useEffect(() => {
    setDropdownUsedFor([
      {
        id: 1,
        name: t("DRINKING_WATER_EUROPE"),
        value: t("DRINKING_WATER_EUROPE"),
        type: t("DRINKING_WATER_EUROPE"),
        unit: "kg",
        typeDB: "drinking_water",
        key: "DRINKING_WATER_EUROPE",
      },
    ]);
  }, [t]);

  const fetchData = useCallback(() => {
    getWaterUsage({
      businessId,
      setObjects: setData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && dropdownUsedFor?.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["type_drinking.value"]?.value || row.type_drinking?.value;

        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );

          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "type_drinking.value": {
        targetField: "qty",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit,
              }));
            }
          }
        },
        dropdownDataKey: "type_drinking.value",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      let typeObj = {};
      if (editedItem["type_drinking.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_drinking.value"].value
        );
      } else if (editedItem["type_drinking"].value) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === editedItem["type_drinking"].value
        );
      }

      const typedb =
        typeObj && typeObj.typeDB
          ? typeObj.typeDB
          : editedItem.type && typeof editedItem.type === "string"
          ? editedItem.type
          : data["typedb_drinking"];
      updateWaterUsed({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        category: category,
        type_drinking: editedItem["type_drinking.value"]
          ? editedItem["type_drinking.value"].value
          : editedItem["type_drinking"]
          ? editedItem["type_drinking"].value
          : null,
        typedb_drinking: typedb,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, data, setData, category]
  );

  const handleAdd = useCallback(
    (newItem) => {
      let typeObj = {};
      if (newItem["type_drinking.value"]) {
        typeObj = dropdownUsedFor.find(
          (item) => item.value === newItem["type_drinking.value"].value
        );
      }
      addWaterToBusiness({
        businessId,
        ...newItem,
        category: category,
        type_drinking: newItem["type_drinking.value"]
          ? newItem["type_drinking.value"].value
          : null,
        typedb_drinking: typeObj ? typeObj.typeDB : newItem.type,

        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, dropdownUsedFor, fetchData, category]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWater({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "type_drinking.value",
        header: `${t("TYPE_WATER")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
        width: "90px",
      },
      {
        accessor: "qty",
        header: t("QUANTITY"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: (rowId) => `${unitMaterials[rowId] || "xx"}`,
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },

      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownUsedFor, unitMaterials, t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_drinking.value": dropdownUsedFor,
    }),
    [dropdownUsedFor]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      subtotalColumns={subtotalColumns}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default DrinkingWaterTable;
